export enum SelfExclusionStatus {
  PENDING_CONFIRMATION = 'PENDING_CONFIRMATION',
  COOLDOWN = 'COOLDOWN',
  AWAITING_FINALIZATION = 'AWAITING_FINALIZATION',
  FINALIZED = 'FINALIZED',
}

export interface UserProfile {
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  postalCode: string | null;
  streetAddress: string | null;
  language?: string | null;
}

export interface User {
  username: string;
  email: string;
  language?: string | null;
  isEmailVerified: boolean;
  isProfileVerified: boolean;
  isKycVerified: boolean;
  createdAt?: string;
  intercomUserJwt?: string;
  unreadNotificationCount?: number;
  profile?: UserProfile | null;
  pendingSelfExclusion?: {
    status: SelfExclusionStatus | string;
    cooldownEndsAt?: string | number;
  } | null;
  balances: {
    balance: string;
    currency: string;
    vault: string;
    withdrawable: string;
    vaultLockedUntil?: string;
    vaultAvailable?: string;
  }[];
  level: {
    next: any;
    current: Level;
    progressPercent: number;
    pointsUntilNext: string;
    hasAvailableSpin: boolean;
    spin: {
      additionalSpinCount: number;
      pendingSpinLevels: string[];
    };
    tierProgress?: {
      currentPoints: string;
      currentTier: {
        tier: string;
        tierName: string;
        firstIndex: number;
      };
      nextTier: any;
      currentTierMinPoints: string;
      nextTierMinPoints: string | null;
      pointsIntoTier: string;
      tierSpanPoints: string | null;
      pointsUntilNextTier: string | null;
      progressPercent: number;
    };
  };
}

export interface Level {
  name: string;
  key: string;
  tier: string;
  tierName?: string;
  index: number;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitializing: boolean;
  error: string | null;
  justRegistered: boolean;
  requiresTwoFactor: boolean;
  twoFactorToken: string | null;
  isMyLevelLoading?: boolean;
}

export interface RegisterPayload {
  username: string;
  email: string;
  password: string;
  referralCode?: string;
}

export interface LoginPayload {
  login: string;
  password: string;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export interface TwoFASetupData {
  secret: string;
  otpauthUrl: string;
  message: string;
}

export interface TwoFASetupResponse {
  data: TwoFASetupData | null;
  error: any;
  message: string;
  status: number;
}

export interface MeResponse {
  status: number;
  message: string;
  data: User;
  error: any;
}

export interface TwoFactorLoginResponseData {
  requiresTwoFactor: boolean;
  twoFactorToken: string;
}

export interface Verify2FAPayload {
  code: string;
  twoFactorToken: string;
}

/**
 * How long a main → vault transfer stays locked. The backend derives the
 * unlock timestamp from the moment of the transfer, so no date is ever sent
 * from here. `none` locks nothing: the funds can go straight back to main, and
 * the transfer skips the approval code (it uses `POST /balance/transfer`).
 */
export type VaultLockDuration = '1d' | '1w' | '1m' | 'none';

export interface TransferPayload {
  fromType: 'main' | 'vault';
  toType: 'main' | 'vault';
  amount: string;
  currencyCode: string;
  /**
   * main → vault only accepts `none` here — locking spans go through the vault
   * request/confirm pair. Must be omitted for vault → main.
   */
  lockDuration?: 'none';
  idempotencyKey?: string;
}

/** Body of step 1 of a locking vault deposit (`POST /balance/vault/request`). */
export interface RequestVaultTransferPayload {
  amount: string;
  currencyCode: string;
  lockDuration: Exclude<VaultLockDuration, 'none'>;
}

/** Which code the player must enter to approve a vault deposit. */
export type VaultTransferChallenge = 'email' | 'authenticator';

export interface RequestVaultTransferResponse {
  challenge: VaultTransferChallenge;
  expiresInSeconds: number;
  /** Only present for the `email` challenge. */
  maskedEmail?: string;
}
